"use client";

import { useCallback, useMemo, useState } from "react";

/**
 * World choropleth showing how widespread climate emergency declarations were
 * by the time Calgary declared. Country fills shade the cedamia jurisdiction
 * count (heavy-tailed: 1 → 653); bins are quantised so the long tail of
 * single-declaration countries doesn't get drowned out by the few bulk
 * declarers (UK 603, Canada 653, South Korea 228).
 *
 * Country outlines are pre-projected to SVG paths in the build script using
 * a simple equirectangular projection. Calgary + Darebin (world-first) are
 * pinned as anchors so the reader can locate Calgary in the global picture.
 */

export type WorldCountry = {
  iso2: string;
  name: string;
  path: string;
  count: number;
  population: number | null;
};

export type WorldPayload = {
  viewbox: [number, number, number, number];
  countries: WorldCountry[];
};

// World-map projection constants — must match WORLD_* in
// scripts/climate/peer_declarations.py so anchor-pin positions register with
// the country paths.
const W_LON_MIN = -170;
const W_LON_MAX = 180;
const W_LAT_MIN = -58;
const W_LAT_MAX = 78;
const W_W = 1000;
const W_H = 500;

function projectWorld(lat: number, lon: number) {
  const x = ((lon - W_LON_MIN) / (W_LON_MAX - W_LON_MIN)) * W_W;
  const y = ((W_LAT_MAX - lat) / (W_LAT_MAX - W_LAT_MIN)) * W_H;
  return { x, y };
}

// Choropleth bins. The cedamia distribution is bimodal-ish: 23 countries with
// just 1 declaration, then a long tail up through 653. Five bins keep the
// "any vs none" distinction prominent without flattening the bulk-declarers.
type Bin = { min: number; max: number; color: string; label: string };
const BINS: Bin[] = [
  { min: 1, max: 1, color: "var(--map-world-1)", label: "1" },
  { min: 2, max: 9, color: "var(--map-world-2)", label: "2–9" },
  { min: 10, max: 49, color: "var(--map-world-3)", label: "10–49" },
  { min: 50, max: 199, color: "var(--map-world-4)", label: "50–199" },
  { min: 200, max: Infinity, color: "var(--map-world-5)", label: "200+" },
];
const ZERO_FILL = "var(--bg-2)";
const ZERO_STROKE = "var(--rule-2)";

function colourFor(count: number): string {
  if (count <= 0) return ZERO_FILL;
  for (const b of BINS) {
    if (count >= b.min && count <= b.max) return b.color;
  }
  return ZERO_FILL;
}

function fmtBigInt(n: number): string {
  return n.toLocaleString("en-CA");
}

export function ClimateWorldMap({
  world,
  calgaryLatLon,
  darebinLatLon,
  calgaryDeclaredOn,
  darebinDeclaredOn,
}: {
  world: WorldPayload;
  calgaryLatLon: [number, number];
  darebinLatLon: [number, number];
  calgaryDeclaredOn: string;
  darebinDeclaredOn: string;
}) {
  const [hover, setHover] = useState<WorldCountry | null>(null);

  const totalDeclaring = useMemo(
    () => world.countries.filter((c) => c.count > 0).length,
    [world.countries],
  );
  const totalJurisdictions = useMemo(
    () => world.countries.reduce((s, c) => s + c.count, 0),
    [world.countries],
  );
  // O(1) iso2 lookup avoids a linear find on every pointer move at ~240
  // countries.
  const byIso = useMemo(() => {
    const m = new Map<string, WorldCountry>();
    for (const c of world.countries) m.set(c.iso2, c);
    return m;
  }, [world.countries]);

  const calgaryXY = projectWorld(calgaryLatLon[0], calgaryLatLon[1]);
  const darebinXY = projectWorld(darebinLatLon[0], darebinLatLon[1]);

  // Hover handlers — clear unless the pointer is over a declaring country.
  // Moving from a declaring country onto a non-declaring one (or out of the
  // <g> entirely) drops the readout; moving between two declarers updates it.
  const handlePointerMove = useCallback(
    (e: React.MouseEvent<SVGGElement>) => {
      const iso = (e.target as SVGElement).getAttribute?.("data-iso");
      if (!iso) {
        setHover((prev) => (prev === null ? prev : null));
        return;
      }
      const c = byIso.get(iso);
      if (c && c.count > 0) {
        setHover((prev) => (prev?.iso2 === c.iso2 ? prev : c));
      } else {
        setHover((prev) => (prev === null ? prev : null));
      }
    },
    [byIso],
  );
  const handlePointerLeave = useCallback(() => setHover(null), []);
  const handleFocus = useCallback(
    (e: React.FocusEvent<SVGGElement>) => {
      const iso = (e.target as SVGElement).getAttribute?.("data-iso");
      if (!iso) return;
      const c = byIso.get(iso);
      if (c && c.count > 0) setHover(c);
    },
    [byIso],
  );
  const handleBlur = useCallback(() => setHover(null), []);

  // The per-path render is invariant in `hover` — the hovered country is
  // drawn separately as an overlay below — so memoising the path layer keeps
  // hover state changes from re-rendering 240 nodes on every mouse move.
  const pathLayer = useMemo(
    () => (
      <g
        onMouseMove={handlePointerMove}
        onMouseLeave={handlePointerLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {world.countries.map((c) => (
          <path
            key={c.iso2}
            d={c.path}
            data-iso={c.iso2}
            fill={colourFor(c.count)}
            stroke={c.count > 0 ? "var(--bg)" : ZERO_STROKE}
            strokeWidth={0.5}
            vectorEffect="non-scaling-stroke"
            style={{ cursor: c.count > 0 ? "pointer" : "default" }}
            tabIndex={c.count > 0 ? 0 : -1}
          >
            <title>
              {c.count > 0
                ? `${c.name} — ${fmtBigInt(c.count)} jurisdiction${c.count === 1 ? "" : "s"}`
                : c.name}
            </title>
          </path>
        ))}
      </g>
    ),
    [
      world.countries,
      handlePointerMove,
      handlePointerLeave,
      handleFocus,
      handleBlur,
    ],
  );

  return (
    <div>
      <div className="cs-mono text-ink-3 text-[11px] uppercase tracking-wider mb-2">
        Climate emergency declarations worldwide — {totalDeclaring} countries,{" "}
        {fmtBigInt(totalJurisdictions)} jurisdictions
      </div>
      <div className="relative cs-card-paper bg-bg rounded-xl overflow-hidden">
        <svg
          viewBox={world.viewbox.join(" ")}
          width="100%"
          height="auto"
          role="img"
          aria-label="World map showing countries with at least one climate emergency declaration, shaded by jurisdiction count"
          className="block"
        >
          {pathLayer}

          {/* Active country highlight overlay so the hovered fill stays on
              top of its neighbours' edges. */}
          {hover && hover.count > 0 ? (
            <path
              d={hover.path}
              fill="none"
              stroke="var(--coral)"
              strokeWidth={1.6}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
              aria-hidden="true"
            />
          ) : null}

          {/* Anchor pins: Darebin (world-first) and Calgary (focus). */}
          <AnchorPin
            x={darebinXY.x}
            y={darebinXY.y}
            label="Darebin"
            sublabel={darebinDeclaredOn}
            colour="var(--ink)"
            anchor="start"
          />
          <AnchorPin
            x={calgaryXY.x}
            y={calgaryXY.y}
            label="Calgary"
            sublabel={calgaryDeclaredOn}
            colour="var(--coral)"
            anchor="end"
          />
        </svg>

        {/* Hover readout — overlay top-right (legend takes bottom-left). */}
        <div
          className="absolute top-3 right-3 bg-white/92 backdrop-blur-sm border border-rule rounded px-3 py-2 text-sm pointer-events-none min-w-[200px] max-w-[260px]"
          aria-live="polite"
        >
          {hover && hover.count > 0 ? (
            <>
              <div className="cs-mono text-ink-3 text-xs">{hover.iso2}</div>
              <div className="font-medium leading-tight">{hover.name}</div>
              <div className="cs-num text-lg mt-1">
                {fmtBigInt(hover.count)}
              </div>
              <div className="cs-mono text-[12px] text-ink-3">
                jurisdiction{hover.count === 1 ? "" : "s"} declared
              </div>
              {hover.population != null ? (
                <div className="mt-1 text-[12px] cs-mono text-ink-3">
                  pop. covered {fmtBigInt(hover.population)}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="cs-mono text-ink-3 text-xs">hover a country</div>
              <div className="cs-num text-lg mt-1">{totalDeclaring}</div>
              <div className="cs-mono text-xs text-ink-3">
                countries with ≥1 declaration
              </div>
            </>
          )}
        </div>

        {/* Legend */}
        <div className="absolute bottom-3 left-3 bg-white/92 backdrop-blur-sm border border-rule rounded px-3 py-2 text-xs">
          <div className="cs-mono text-ink-3 mb-1.5">jurisdictions</div>
          <div className="flex items-center gap-1">
            <div className="flex flex-col items-center">
              <div
                className="w-7 h-3"
                style={{ background: ZERO_FILL, border: `1px solid ${ZERO_STROKE}` }}
              />
              <div className="cs-mono text-[11px] mt-0.5 text-ink-3">0</div>
            </div>
            {BINS.map((b) => (
              <div key={b.label} className="flex flex-col items-center">
                <div
                  className="w-7 h-3"
                  style={{ background: b.color, border: "1px solid var(--bg)" }}
                />
                <div className="cs-mono text-[11px] mt-0.5 text-ink-3">
                  {b.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnchorPin({
  x,
  y,
  label,
  sublabel,
  colour,
  anchor,
}: {
  x: number;
  y: number;
  label: string;
  sublabel: string;
  colour: string;
  anchor: "start" | "end";
}) {
  const dx = anchor === "end" ? -10 : 10;
  return (
    <g pointerEvents="none">
      <circle cx={x} cy={y} r={9} fill={colour} opacity={0.18} />
      <circle cx={x} cy={y} r={4.5} fill={colour} />
      <text
        x={x + dx}
        y={y - 7}
        fontSize={12}
        className="cs-mono"
        fill="var(--ink)"
        textAnchor={anchor}
      >
        {label}
      </text>
      <text
        x={x + dx}
        y={y + 7}
        fontSize={10}
        className="cs-mono"
        fill="var(--ink-3)"
        textAnchor={anchor}
      >
        {sublabel}
      </text>
    </g>
  );
}
